import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QICK_APP_LOGIN_URL, QICK_APP_REGISTER_URL } from "@/lib/routes";

const LOGO_URL = "/brand/qick-logo.png";

const NAV_LINKS = [
    { label: "Hoe werkt het", href: "#hoe-werkt-het" },
    { label: "Branches", href: "#branches" },
    { label: "Work & Connect", href: "#qick-work" },
    { label: "Prijzen", href: "#pricing" },
    { label: "Contact", href: "#contact" },
];

const scrollToId = (id) => {
    const el = document.querySelector(id);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (el) el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
};

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleLink = (e, href) => {
        e.preventDefault();
        setOpen(false);
        scrollToId(href);
    };

    const navLinkClass = scrolled
        ? "text-slate-600 after:bg-[#0251B7] hover:text-[#0251B7]"
        : "text-white/90 after:bg-white hover:text-white";
    const loginClass = scrolled
        ? "text-slate-700 hover:bg-blue-50 hover:text-[#0251B7]"
        : "text-white hover:bg-white/12 hover:text-white";
    const logoTextClass = scrolled ? "text-[#0B1528]" : "text-white";
    const mobileToggleClass = scrolled
        ? "text-slate-700 hover:bg-blue-50 hover:text-[#0251B7]"
        : "text-white hover:bg-white/12 hover:text-white";
    const ctaClass = scrolled
        ? "bg-[#0251B7] text-white hover:bg-[#023D8A] shadow-[0_10px_24px_-12px_rgba(2,81,183,0.7)] hover:shadow-[0_16px_30px_-14px_rgba(2,81,183,0.8)] focus-visible:ring-[#0251B7]/40"
        : "bg-white text-[#0251B7] hover:bg-blue-50 hover:text-[#023D8A] shadow-[0_14px_30px_-16px_rgba(0,26,67,0.6)] hover:shadow-[0_18px_34px_-16px_rgba(0,26,67,0.68)] focus-visible:ring-white/70";

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_14px_40px_-32px_rgba(15,23,42,0.45)]"
                    : "bg-[#0251B7]/20 backdrop-blur-xl border-b border-white/16 shadow-[0_18px_50px_-38px_rgba(0,26,67,0.72)]"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <nav className="h-16 md:h-20 flex items-center justify-between">
                    <a
                        href="#top"
                        onClick={(e) => handleLink(e, "#top")}
                        data-testid="nav-logo"
                        className="flex items-center gap-2.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-4"
                    >
                        <span className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-[#0251B7] shadow-sm ring-1 ring-white/16">
                            <img
                                src={LOGO_URL}
                                alt="QICK!"
                                className="h-10 w-10 rounded-xl object-cover"
                            />
                        </span>
                        <span className={`text-lg font-bold ${logoTextClass}`}>
                            QICK!
                        </span>
                    </a>

                    <div className="hidden md:flex items-center gap-5 lg:gap-7">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                onClick={(e) => handleLink(e, link.href)}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`relative text-sm font-semibold transition-colors after:absolute after:-bottom-2 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:rounded-full after:transition-transform hover:after:scale-x-100 ${navLinkClass}`}
                            >
                                {link.label}
                            </a>
                        ))}
                    </div>

                    <div className="hidden md:flex items-center gap-3">
                        <a
                            href={QICK_APP_LOGIN_URL}
                            data-testid="nav-login-link"
                            className={`inline-flex h-10 items-center rounded-full px-4 text-sm font-semibold transition-all active:scale-[0.98] ${loginClass}`}
                        >
                            Inloggen
                        </a>
                        <Button
                            asChild
                            data-testid="nav-start-trial-btn"
                            className={`h-auto rounded-full px-5 py-2.5 font-semibold transition-all hover:-translate-y-0.5 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-offset-2 ${ctaClass}`}
                        >
                            <a href={QICK_APP_REGISTER_URL}>Start gratis</a>
                        </Button>
                    </div>

                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className={`inline-flex h-10 w-10 items-center justify-center rounded-lg transition-all active:scale-[0.98] md:hidden ${mobileToggleClass}`}
                        aria-label="Menu openen of sluiten"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </nav>
            </div>

            {open && (
                <div className="md:hidden bg-white border-t border-slate-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
                        {NAV_LINKS.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                onClick={(e) => handleLink(e, link.href)}
                                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="px-3 py-2.5 rounded-lg text-base font-semibold text-slate-700 hover:bg-blue-50 hover:text-[#0251B7]"
                            >
                                {link.label}
                            </a>
                        ))}
                        <a
                            href={QICK_APP_LOGIN_URL}
                            data-testid="mobile-nav-login-link"
                            className="px-3 py-2.5 rounded-lg text-base font-semibold text-slate-700 hover:bg-blue-50 hover:text-[#0251B7]"
                        >
                            Inloggen
                        </a>
                        <Button
                            asChild
                            data-testid="mobile-nav-start-trial-btn"
                            className="mt-2 rounded-full bg-[#0251B7] hover:bg-[#023D8A] text-white font-semibold shadow-[0_10px_24px_-12px_rgba(2,81,183,0.7)] active:scale-[0.98]"
                        >
                            <a href={QICK_APP_REGISTER_URL}>Start gratis</a>
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
